/**
 * Collection Discount % Sorting
 *
 * Implements client-side sorting of collection products by discount percentage.
 *
 * Features:
 * - Sorts products by maximum discount (highest first)
 * - Zero CLS (Cumulative Layout Shift) using DocumentFragment
 * - URL persistence via sort_by parameter
 * - Works with AJAX filtering
 *
 * @version 1.0.0
 */

class CollectionDiscountSort {
  constructor() {
    this.sortDropdown = document.querySelector('#sort-by');
    this.productGrid = document.querySelector('#product-grid-ajax');

    if (!this.sortDropdown || !this.productGrid) {
      console.warn('CollectionDiscountSort: Required elements not found');
      return;
    }

    this.init();
  }

  init() {
    // Listen for dropdown changes
    this.sortDropdown.addEventListener('change', (e) => {
      if (e.target.value === 'discount_desc') {
        this.sortByDiscount();
      }
    });

    // Apply sort on page load if URL parameter is set
    const urlParams = new URLSearchParams(window.location.search);
    const sortBy = urlParams.get('sort_by');

    if (sortBy === 'discount_desc') {
      this.sortByDiscount();
      // Ensure dropdown shows correct value
      this.sortDropdown.value = 'discount_desc';
    } else if (!sortBy && document.body.dataset.closingSale === 'true') {
      // Auto-sort by discount during closing sale when no explicit sort
      this.sortByDiscount();
      this.sortDropdown.value = 'discount_desc';
    }
  }

  sortByDiscount() {
    // Get all product cards
    const productCards = Array.from(this.productGrid.querySelectorAll('.card--product'));

    if (productCards.length === 0) {
      console.warn('CollectionDiscountSort: No product cards found');
      return;
    }

    // Sort by discount percentage (highest first)
    const sortedCards = productCards.sort((a, b) => {
      const discountA = parseInt(a.getAttribute('data-discount-percentage') || '0');
      const discountB = parseInt(b.getAttribute('data-discount-percentage') || '0');

      // Sort descending (highest discount first)
      if (discountB !== discountA) {
        return discountB - discountA;
      }

      // Tie-breaking: maintain original order (stable sort)
      // Get original index from DOM position
      const indexA = productCards.indexOf(a);
      const indexB = productCards.indexOf(b);
      return indexA - indexB;
    });

    // Reorder DOM with zero CLS using DocumentFragment
    this.reorderProducts(sortedCards);

    // Update URL parameter
    this.updateURL('discount_desc');
  }

  /**
   * Reorder products in DOM with zero CLS
   * Uses DocumentFragment for atomic DOM manipulation
   */
  reorderProducts(sortedCards) {
    // Create DocumentFragment for atomic DOM update
    const fragment = document.createDocumentFragment();

    // Append all cards to fragment in sorted order
    sortedCards.forEach(card => {
      fragment.appendChild(card);
    });

    // Clear grid and append fragment in single reflow
    this.productGrid.innerHTML = '';
    this.productGrid.appendChild(fragment);
  }

  /**
   * Update URL with sort parameter
   * Maintains existing query parameters
   */
  updateURL(sortValue) {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortValue);

    // Update URL without page reload
    window.history.pushState({}, '', url.toString());
  }

  /**
   * Re-apply sort after AJAX grid refresh
   * Called by MutationObserver or AJAX complete callback
   */
  reapplySort() {
    const urlParams = new URLSearchParams(window.location.search);
    const sortBy = urlParams.get('sort_by');

    if (sortBy === 'discount_desc') {
      this.sortByDiscount();
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.collectionDiscountSort = new CollectionDiscountSort();
  });
} else {
  window.collectionDiscountSort = new CollectionDiscountSort();
}
